import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, Play, CheckCircle, Clock, BookOpen, Target,
  Star, Lightbulb, Award, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const CoachingModule = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModuleData();
  }, [moduleId]);

  const fetchModuleData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/coaching/modules/${moduleId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setModuleData(response.data);
    } catch (error) {
      console.error('Error fetching module data:', error);
      if (error.response?.status === 402) {
        navigate('/subscription/plans');
      } else if (error.response?.status === 404) {
        navigate('/coaching/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLessonClick = (lessonId) => {
    navigate(`/coaching/lessons/${lessonId}`);
  };

  const getContentTypeIcon = (contentType) => {
    switch (contentType) {
      case 'interactive':
        return <Target className="w-5 h-5 text-blue-500" />;
      case 'guided_exercise':
        return <Lightbulb className="w-5 h-5 text-yellow-500" />;
      case 'video_guide':
        return <Play className="w-5 h-5 text-green-500" />;
      case 'practice_session':
        return <Award className="w-5 h-5 text-purple-500" />;
      case 'analysis_tool':
        return <Star className="w-5 h-5 text-orange-500" />;
      default:
        return <BookOpen className="w-5 h-5 text-gray-500" />;
    }
  };

  const getContentTypeLabel = (contentType) => {
    switch (contentType) {
      case 'interactive':
        return 'Interactive';
      case 'guided_exercise':
        return 'Exercise';
      case 'video_guide':
        return 'Video Guide';
      case 'practice_session':
        return 'Practice';
      case 'analysis_tool':
        return 'Analysis';
      case 'writing_workshop':
        return 'Workshop';
      case 'scenario_practice':
        return 'Scenarios';
      case 'guided_reflection':
        return 'Reflection';
      case 'self_assessment':
        return 'Assessment';
      case 'framework_learning':
        return 'Framework';
      default:
        return 'Lesson';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-rose-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-white">Loading module content...</p>
        </div>
      </div>
    );
  }

  if (!moduleData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Module Not Found</h2>
          <Button onClick={() => navigate('/coaching/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const { module, progress } = moduleData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(236,72,153,0.1),transparent_70%)]"></div>
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/coaching/dashboard')}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Dashboard
          </Button>
          
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            VIP Coaching
          </Badge>
        </div>

        {/* Module Header */}
        <Card className="bg-white/5 backdrop-blur-md border-white/10 mb-8">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <div className="text-4xl mr-4">{module.icon}</div>
                <div>
                  <CardTitle className="text-2xl text-white mb-2">{module.title}</CardTitle>
                  <p className="text-gray-300">{module.description}</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-white mb-2">
                  <span>Module Progress</span>
                  <span>{Math.round((progress.completed_lessons / progress.total_lessons) * 100)}%</span>
                </div>
                <Progress 
                  value={(progress.completed_lessons / progress.total_lessons) * 100} 
                  className="h-3"
                />
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">
                  {progress.completed_lessons} of {progress.total_lessons} lessons completed
                </span>
                <div className="flex items-center text-yellow-400">
                  <Award className="w-4 h-4 mr-1" />
                  <span>50 points upon completion</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lessons */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white mb-4">Lessons</h2>
          
          {module.lessons.map((lesson, index) => (
            <Card 
              key={lesson.id}
              className={`bg-white/5 backdrop-blur-md border-white/10 hover:border-white/20 transition-all duration-300 ${
                lesson.is_available ? 'cursor-pointer' : 'opacity-60'
              }`}
              onClick={() => lesson.is_available && handleLessonClick(lesson.id)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-rose-500 to-pink-500 rounded-full mr-4 text-white font-bold">
                      {lesson.is_completed ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-1">{lesson.title}</h3>
                      <p className="text-gray-300 text-sm mb-2">{lesson.description}</p>
                      
                      <div className="flex items-center space-x-4 text-xs text-gray-400">
                        <div className="flex items-center">
                          {getContentTypeIcon(lesson.content_type)}
                          <span className="ml-1">{getContentTypeLabel(lesson.content_type)}</span>
                        </div>
                        
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          <span>{lesson.estimated_time}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    {lesson.is_completed ? (
                      <Badge className="bg-green-500/20 text-green-400 border-green-500/30 mr-3">
                        Completed
                      </Badge>
                    ) : lesson.is_available ? (
                      <Button size="sm" className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 mr-3">
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    ) : (
                      <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30 mr-3">
                        Locked
                      </Badge>
                    )}
                    
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Module Benefits */}
        <Card className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-purple-500/20 mt-8">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Star className="w-5 h-5 mr-2 text-yellow-500" />
              What You'll Achieve
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Build lasting confidence</span>
                </div>
                <div className="flex items-center text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Practical skills you can use immediately</span>
                </div>
                <div className="flex items-center text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Personalized coaching insights</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Progress tracking and analytics</span>
                </div>
                <div className="flex items-center text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Earn coaching points and achievements</span>
                </div>
                <div className="flex items-center text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Unlock advanced features</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CoachingModule;